import { SUPPLIER_GET, SUPPLIER_UPDATE } from '@constants';
import {
  SupplierForm,
  toSupplierPayload,
  fromApiSupplier,
} from '@forms/SupplierForm';
import { SupplierFormValues } from '@forms/SupplierForm/types';
import { useAuthHttp } from '@hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';

export function EditSupplier() {
  useDocumentTitle('FLOX - Edit Supplier');
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] =
    useState<Partial<SupplierFormValues>>();
  // The read-model record carries MDM fields the form does not edit; keep it so
  // the update payload preserves them instead of overwriting with defaults.
  const [originalSupplier, setOriginalSupplier] =
    useState<Record<string, unknown>>();

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Suppliers', href: '/supplier-master', current: false },
    {
      name: 'Edit Supplier',
      href: `/supplier-master/edit/${id}`,
      current: true,
    },
  ];

  useEffect(() => {
    if (!id) return undefined;
    let isStillOnThisSupplier = true;

    const fetchSupplier = async () => {
      setIsLoading(true);
      setOriginalSupplier(undefined);
      setDefaultValues(undefined);
      try {
        const { data } = await authHttp.post(SUPPLIER_GET, { id });

        const supplier = data?.suppliers?.[0];
        if (supplier && isStillOnThisSupplier) {
          setOriginalSupplier(supplier);
          setDefaultValues({
            supplierId: supplier.supplierId || '',
            name: supplier.name || '',
            type: supplier.type || '',
            contactName: supplier.contactName || '',
            contactEmail: supplier.contactEmail || '',
            contactNumber: supplier.contactNumber || '',
            hqId: supplier.hqId || '',
            hqName: supplier.hqName || '',
            ...fromApiSupplier(supplier),
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        if (isStillOnThisSupplier) setIsLoading(false);
      }
    };

    fetchSupplier();
    return () => {
      isStillOnThisSupplier = false;
    };
  }, [id]);

  const handleOnSubmit = async (data: SupplierFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: updateSupplierData } = await authHttp.post(
        SUPPLIER_UPDATE,
        {
          supplier: {
            ...originalSupplier,
            ...toSupplierPayload(data),
            id,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (updateSupplierData) {
        navigate('/supplier-master');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-[calc(100vh-12rem)]'>
        <div className='flex flex-col items-center gap-3'>
          <FloxSpinner size='md' />
          <p className='text-gray-500 text-sm'>Loading supplier...</p>
        </div>
      </div>
    );
  }

  if (!defaultValues) {
    return (
      <div className='flex items-center justify-center h-[calc(100vh-12rem)]'>
        <p className='text-gray-500'>Supplier not found</p>
      </div>
    );
  }

  return (
    <SupplierForm
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      header='Edit Supplier'
      pages={pages}
      defaultValues={defaultValues}
    />
  );
}
